/**
 * © [2023] Conduent, Inc. All rights reserved. Conduent®and Conduent Agile
 * Star®are trademarks of Conduent, Inc. and/or its subsidiaries in the United
 * States and/or other countries.
 */
import moment from 'moment';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const contentFont = 10;
const pageMargins = [42, 80, 28.8, 25.92];
const styles = {
  header: {
    fontSize: 18,
    bold: true,
    margin: [0, 0, 0, 10],
  },
  subheader: {
    fontSize: 16,
    bold: true,
    margin: [0, 10, 0, 5],
  },
  tableExample: {
    margin: [0, 5, 0, 15],
  },
  tableHeader: {
    bold: true,
    fontSize: 13,
    color: 'black',
  },
};

const numberFormatter = (value, prefix = '', suffix = '') => {
  if (value < 0) {
    return `(${prefix} ${value
      .toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      .substring(1)} ${suffix})`;
  }
  return `${prefix} ${parseFloat(value)?.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${suffix}`;
};

const getHeader = (currentPage, pageCount, data, options) => {
  const { prinDate, requestedBy, reportName } = data;

  const { pageSize, info } = options;
  const {
    firstInfo = '',
    secondInfo = '',
    thirdInfo = '',
    fourthInfo = '',
    fifthInfo = '',
  } = info;

  let widths = [255, 255, 255];

  const pageWidth = 13 * 140;
  widths = [pageWidth / 3, pageWidth / 3, pageWidth / 3];
  const header = {
    alignment: 'center',
    fontSize: 10,
    margin: [38.8, 20, 28.8, 25.92],
    style: 'tableExample',
    table: {
      widths: widths,
      body: [
        [
          {
            text: [{ text: 'Requested By: ', bold: true }, requestedBy],
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'left',
          },
          {
            text: reportName,
            fontSize: 12,
            bold: true,
            fontFamily: 'arial',
            alignment: 'center',
          },
          {
            text: [{ text: 'Date Printed: ', bold: true }, prinDate],
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'right',
          },
        ],
        [
          {
            text: firstInfo,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'left',
          },
          {
            text: thirdInfo,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'center',
            bold: true,
          },
          {
            text: `Page ${currentPage} of ${pageCount}`,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'right',
          },
        ],
        [
          {
            text: secondInfo,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'left',
          },
          {
            text: fourthInfo,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'center',
            bold: true,
          },
          {
            text: fifthInfo,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'right',
          },
        ],
      ],
    },
    layout: 'noBorders',
  };
  return header;
};

const getTableBodyData = (data, allCols, page) => {
  const dataToReturn = [];
  const totaRecordCount = 0;
  let cols = allCols;
  const secondHeader = [];
  if (allCols[0].children) {
    cols = [];
    for (let i = 0; i < allCols.length; i += 1) {
      for (let j = 0; j < allCols[i].children.length; j += 1) {
        cols.push(allCols[i].children[j]);
        if (j == 0) {
          secondHeader.push(allCols[i].headerName);
        } else {
          secondHeader.push('');
        }
      }
    }
  }

  let columns = [];
  if (secondHeader.length > 0) {
    for (let i = 0; i < secondHeader.length; i += 1) {
      let rightBorder = false;
      if (i == 0 || i == 5 || i == 9 || i == 11 || i == 12) {
        rightBorder = true;
      }
      columns.push({
        colSpan: i === 1 ? 5 : i === 6 ? 4 : i === 10 ? 2 : 1,
        text: secondHeader[i],
        fontSize: contentFont,
        border: [true, true, rightBorder, true],
        bold: true,
        alignment: 'center',
      });
    }
    dataToReturn.push(columns);
    columns = [];
  }
  for (let i = 0; i < cols.length; i += 1) {
    let rightBorder = false;
    if (i == 0 || i == 5 || i == 9 || i == 11 || i == 12) {
      rightBorder = true;
    }
    columns.push({
      text: page === 1 && i == 0 ? 'Rebate Program' : cols[i].headerName,
      fontSize: contentFont,
      border: [i == 0, false, true, true],
      bold: true,
      alignment: cols[i].alignment || 'left',
    });
  }
  dataToReturn.push(columns);
  for (let i = 0; i < data.length; i += 1) {
    const tempCols = [];

    for (let j = 0; j < cols.length; j += 1) {
      let rightBorder = false;
      if (j == 0 || j == 5 || j == 9 || j == 11 || j == 12) {
        rightBorder = true;
      }
      let text = data[i][cols[j].field];
      if (j === 0 && text === undefined) {
        text = data[i].rebateProgram;
      }
      const boolValue = data[i]?.bold || false;
      if (typeof text === 'number') {
        text = numberFormatter(
          text,
          cols[j].printPrefixValue,
          cols[j].printPostfixValue
        );
      }
      tempCols.push({
        text,
        fontSize: boolValue ? contentFont : contentFont - 1,
        border: [j == 0, boolValue, true, boolValue],
        bold: boolValue,
        alignment: cols[j].alignment || 'left',
        color:
          typeof data[i][cols[j].field] === 'number' &&
          data[i][cols[j].field] < 0
            ? '#a81f00'
            : 'black',
      });
    }
    dataToReturn.push(tempCols);
  }
  return dataToReturn;
};
const getTableBody = (data, cols, paginationArr, colWidths, pageHeading) => {
  const body = [];
  const dataKeys = paginationArr.length > 0 ? paginationArr : Object.keys(data);
  const widths = colWidths.length > 0 ? colWidths : cols.map((item) => '*');
  //   dataKeys.sort();
  for (let i = 0; i < dataKeys.length; i += 1) {
    body.push({
      text: [
        {
          text: `${pageHeading} A${i < 3 ? i + 1 : 3}: `,
          bold: true,
          fontSize: contentFont,
        },
        { text: dataKeys[i], fontSize: contentFont },
      ],
    });
    body.push({
      style: 'tableExample',
      table: {
        widths: ['*'],
        body: [
          [
            {
              style: 'tableExample',
              table: {
                headerRows: cols[0].children ? 2 : 1,
                widths: widths,
                body: getTableBodyData(data[dataKeys[i]], cols, i, widths),
              },
            },
          ],
        ],
      },
      layout: 'noBorders',
    });
    if (i < dataKeys.length - 1)
      body.push({
        text: '',
        fontSize: 14,
        bold: true,
        pageBreak: 'before',
        margin: [0, 0, 0, 8],
      });
  }
  return body;
};

/**
 * Download as PDF with all Data.
 * @param {object} headerData - conatisn header info of PDF.
 * @param {object} bodyData - constains Body data for PDF.
 */
const getPdfData = (
  bodyData,
  columns,
  headerData,
  paginationArr,
  widths,
  pageHeading,
  options
) => {
  const { pageSize } = options;
  const content = getTableBody(
    bodyData,
    columns,
    paginationArr,
    widths,
    pageHeading
  );
  const currentYear = new Date().getFullYear();
  let docDefinition = {
    header: (currentPage, pageCount) => {
      return getHeader(currentPage, pageCount, headerData, options);
    },
    footer: () => {
        const footers = [];
        footers.push({
          text:`${currentYear} Conduent Business Services, LLC. All rights reserved. Conduent and Conduent Agile Star are trademarks of Conduent Business Services, LLC in the United States and/or other countries.`,
          fontSize: 8,
          color: '#636363',
          fontFamily: 'Arial',
          alignment: 'center',
        });

      return footers;
    },
    content,
    pageMargins,
    styles,
    pageOrientation: 'landscape',
  };

  if (pageSize && pageSize?.width && pageSize.height) {
    docDefinition = {
      ...docDefinition,
      pageSize: {
        width: 13 * 150,
        height: pageSize.height,
      },
    };
  }
  return docDefinition;
};

/**
 * Download as PDF with all Data.
 * @param {object} data - header info of PDF.
 * @param {object} userDefaultValues - use info containg object
 */
const getHeaderData = (data, UserDefaultsValues, logInUser) => {
  const { reportName } = data;
  return {
    prinDate: moment(new Date()).format('MM/DD/YYYY'),
    requestedBy:
      UserDefaultsValues && UserDefaultsValues.userId
        ? UserDefaultsValues.userId
        : logInUser,
    reportName,
  };
};

/**
 * Download as PDF with all Data.
 * @param {object} header - header info of PDF.
 * @param {object} bodyData - All the data for the PDF.
 * @param {object} userDefaultValues
 */
export const exportAllPDF = (
  header,
  bodyData,
  columns,
  userDefaultValues,
  options = {},
  paginationArr = [],
  pageHeading,
  widths,
  logInUser
) => {
  const headerData = getHeaderData(header, userDefaultValues, logInUser);

  const pdfData = getPdfData(
    bodyData,
    columns,
    headerData,
    paginationArr,
    widths,
    pageHeading,
    options
  );
  pdfMake
    .createPdf(pdfData)
    .download(
      `${header?.fileName ? header.fileName : header?.reportName || 'File'}.pdf`
    );
};
